// data-fit LLC · DecisionFlow · EXECUTE Stage
// PayrollExportManager.jsx · DDL: R5 · API: v1.11 · Session 92
// Auth: useAuth() direct — ARCH-2 compliant
// Key constraints: no calculation_run_id, SELF_APPROVAL_BLOCKED named handling, export_format enum
// PATCH-S92-06: SCREEN_ORDER imported for pattern consistency with all other EXECUTE screens
// M040 C2: hardcoded colors → tokens, text-* → text-tok-*, all <td> get text-[rgb(var(--color-text))]

import React, { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../auth/AuthContext.jsx'
import { SCREEN_ORDER } from './executeFlowConstants'

const EXPORT_FORMATS = ['csv', 'json', 'fixed_width']
const EXPORT_STATUSES = ['draft', 'approved', 'exported']

function getErrorMessage(payload) {
  const error = payload?.error
  if (!error) return 'Unexpected payroll export error.'
  if (error.code === 'SELF_APPROVAL_BLOCKED') return 'Payroll exports cannot be approved by their creator. Ask a second comp admin to approve this batch.'
  if (error.code === 'FINANCE_APPROVAL_REQUIRED') return 'Finance plan approval is required for this fiscal period before export. Ask your Finance Admin to approve the Finance Plan Input, then retry.'
  if (error.code === 'EXPORT_NOT_READY') return 'Export file is not yet ready. Refresh the batch and try the download again.'
  if (error.code === 'PAYROLL_EXPORT_BATCH_EXISTS') return 'A payroll export batch already exists for this statement batch.'
  if (error.code === 'PAYROLL_EXPORT_BATCH_NOT_FOUND') return 'Payroll export batch was not found.'
  if (error.code === 'STATEMENT_BATCH_NOT_RELEASED') return 'The selected statement batch must be released before export.'
  if (error.code === 'INVALID_STATUS_TRANSITION') return error.recovery_action || 'Invalid payroll export status transition.'
  if (error.code === 'AUTH_INSUFFICIENT_ROLE') return 'You do not have the comp_admin role required for payroll exports.'
  if (error.code === 'FEATURE_UNAVAILABLE') return 'Payroll export is unavailable for the current tenant tier.'
  return error.recovery_action || error.message || 'Unable to manage payroll export.'
}

export default function PayrollExportManager({ fiscalPeriodId, fiscalPeriodLabel }) {
  const { token, user } = useAuth()

  const [batches, setBatches] = useState([])
  const [selectedBatch, setSelectedBatch] = useState(null)
  const [statementBatchId, setStatementBatchId] = useState('')
  const [statementBatches, setStatementBatches] = useState([])
  const [exportFormat, setExportFormat] = useState('csv')
  const [confirmation, setConfirmation] = useState(null)
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  const periodId = useMemo(() => { const parsed = Number(fiscalPeriodId); return Number.isInteger(parsed) ? parsed : null }, [fiscalPeriodId])
  const currentUserId = user?.id || user?.user_id || user?.email || null

  const loadBatches = async () => {
    setLoading(true); setErrorMessage('')
    try {
      const url = periodId
        ? `/api/v1/payroll-export-batches?fiscal_period_id=${encodeURIComponent(periodId)}`
        : '/api/v1/payroll-export-batches'
      const response = await fetch(url, { credentials: 'include' })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(getErrorMessage(payload))
      setBatches(Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [])
    } catch (error) {
      setErrorMessage(error.message || 'Unable to load payroll exports.')
    } finally {
      setLoading(false)
    }
  }

  const loadSingleBatch = async (id) => {
    if (!id) return
    setLoading(true); setErrorMessage('')
    try {
      const response = await fetch(`/api/v1/payroll-export-batches/${id}`, { credentials: 'include' })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(getErrorMessage(payload))
      setSelectedBatch(payload?.data || payload)
    } catch (error) {
      setErrorMessage(error.message || 'Unable to load payroll export batch.')
    } finally {
      setLoading(false)
    }
  }

  const createBatch = async () => {
    setErrorMessage(''); setSuccessMessage('')
    if (!periodId) { setErrorMessage('A valid integer fiscal_period_id is required.'); return }
    if (!statementBatchId.trim()) { setErrorMessage('Select a released statement batch before creating the export.'); return }
    if (!EXPORT_FORMATS.includes(exportFormat)) { setErrorMessage("export_format must be 'csv', 'json', or 'fixed_width'."); return }
    setLoading(true)
    try {
      const response = await fetch('/api/v1/payroll-export-batches', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fiscal_period_id: Number(periodId), statement_batch_id: statementBatchId.trim(), export_format: exportFormat }),
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(getErrorMessage(payload))
      const nextBatch = payload?.data || payload
      setSelectedBatch(nextBatch)
      setBatches((current) => [nextBatch, ...current])
      setSuccessMessage('Payroll export batch created in draft status.')
      // OI-PAYROLL-EXPORT-DROPDOWN-RESET: reload batch list after creation so new batch
      // appears without manual refresh. statementBatchId intentionally NOT cleared —
      // CA may need to create multiple export batches for the same statement batch.
      await loadBatches()
    } catch (error) {
      setErrorMessage(error.message || 'Unable to create payroll export batch.')
    } finally {
      setLoading(false)
    }
  }

  const approveBatch = async () => {
    if (!selectedBatch?.payroll_export_batch_id) return
    setLoading(true); setErrorMessage(''); setSuccessMessage('')
    try {
      const response = await fetch(`/api/v1/payroll-export-batches/${selectedBatch.payroll_export_batch_id}/approve`, { method: 'POST', credentials: 'include' })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(getErrorMessage(payload))
      const nextBatch = payload?.data || { ...selectedBatch, export_status: 'approved' }
      setSelectedBatch(nextBatch)
      setBatches((current) => current.map((item) => (item.payroll_export_batch_id === nextBatch.payroll_export_batch_id ? nextBatch : item)))
      setConfirmation(null)
      setSuccessMessage('Payroll export batch approved.')
    } catch (error) {
      setErrorMessage(error.message || 'Unable to approve payroll export.')
    } finally {
      setLoading(false)
    }
  }

  const exportBatch = async () => {
    if (!selectedBatch?.payroll_export_batch_id) return
    setLoading(true); setErrorMessage(''); setSuccessMessage('')
    try {
      const response = await fetch(`/api/v1/payroll-export-batches/${selectedBatch.payroll_export_batch_id}/export`, { method: 'POST', credentials: 'include' })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(getErrorMessage(payload))
      const nextBatch = payload?.data || { ...selectedBatch, export_status: 'exported' }
      setSelectedBatch(nextBatch)
      setBatches((current) => current.map((item) => (item.payroll_export_batch_id === nextBatch.payroll_export_batch_id ? nextBatch : item)))
      setConfirmation(null)
      setSuccessMessage('Payroll export batch exported.')
    } catch (error) {
      setErrorMessage(error.message || 'Unable to export payroll batch.')
    } finally {
      setLoading(false)
    }
  }

  const loadStatementBatches = async () => {
    if (!periodId) return
    try {
      const response = await fetch(
        `/api/v1/statement-batches?fiscal_period_id=${encodeURIComponent(periodId)}&status=released`,
        { credentials: 'include' }
      )
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) return
      const data = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : []
      setStatementBatches(data)
      // Auto-select the first released batch if only one exists and none selected yet
      if (data.length === 1 && !statementBatchId) {
        setStatementBatchId(data[0].statement_batch_id)
      }
    } catch (_) { /* non-fatal — user can still paste UUID */ }
  }

  useEffect(() => {
    loadBatches()
    loadStatementBatches()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodId])

  const status = EXPORT_STATUSES.includes(selectedBatch?.export_status) ? selectedBatch.export_status : 'draft'
  const createdBy = selectedBatch?.created_by || selectedBatch?.created_by_user_id
  const approvalActorWarning = currentUserId && createdBy && String(currentUserId) === String(createdBy)

  const requestApprove = () => setConfirmation({
    title: 'Approve payroll export?',
    message: approvalActorWarning ? 'Actor separation advisory: the current user appears to be the creator. Server may return SELF_APPROVAL_BLOCKED.' : 'This moves the payroll export from draft to approved.',
    confirmLabel: 'Approve export', onConfirm: approveBatch,
  })

  const requestExport = () => setConfirmation({
    title: 'Export payroll batch?',
    message: 'This moves the payroll export from approved to exported. Requires Finance Plan Input to be approved for this fiscal period — the server will return FINANCE_APPROVAL_REQUIRED if not.',
    confirmLabel: 'Export batch', onConfirm: exportBatch,
  })

  void SCREEN_ORDER

  return (
    <section className="space-y-6">
      <header>
        <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-primary))] mb-1">EXEC-06</p>
        <h2 className="text-tok-2xl font-bold text-[rgb(var(--color-text))]">Payroll Export Manager</h2>
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mt-1">Create, approve, and export payroll batches.</p>
      </header>

      {errorMessage && <div className="rounded-md bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 text-[rgb(var(--color-danger))] px-4 py-3 text-tok-sm">{errorMessage}</div>}
      {successMessage && <div className="rounded-md bg-[rgb(var(--color-success))]/10 border border-[rgb(var(--color-success))]/30 text-[rgb(var(--color-success))] px-4 py-3 text-tok-sm">{successMessage}</div>}

      <div className="bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] rounded-lg p-6 space-y-4">
        <label className="block text-tok-sm font-medium text-[rgb(var(--color-text))]">
          Fiscal Period
          <input value={fiscalPeriodLabel || periodId || ''} readOnly className="mt-1 block w-full rounded border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text-muted))]" />
        </label>
        <label className="block text-tok-sm font-medium text-[rgb(var(--color-text))]">
          Statement Batch
          {statementBatches.length > 0 ? (
            <select
              value={statementBatchId}
              onChange={(e) => setStatementBatchId(e.target.value)}
              className="mt-1 block w-full rounded border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm focus:outline-none"
            >
              <option value="">Select statement batch…</option>
              {statementBatches.map((b) => (
                <option key={b.statement_batch_id} value={b.statement_batch_id}>
                  {b.statement_batch_id.slice(0, 8)}… — {b.row_count ?? b.count ?? '?'} positions — released {b.released_at ? new Date(b.released_at).toLocaleDateString() : ''}
                </option>
              ))}
            </select>
          ) : (
            <input
              value={statementBatchId}
              onChange={(e) => setStatementBatchId(e.target.value)}
              placeholder="No released batches found — paste UUID"
              className="mt-1 block w-full rounded border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm focus:outline-none"
            />
          )}
        </label>
        <label className="block text-tok-sm font-medium text-[rgb(var(--color-text))]">
          Export Format
          <select value={exportFormat} onChange={(e) => setExportFormat(e.target.value)} className="mt-1 block w-full rounded border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm focus:outline-none">
            <option value="csv">csv</option>
            <option value="json">json</option>
            <option value="fixed_width">fixed_width</option>
          </select>
        </label>
        <button type="button" onClick={createBatch} disabled={loading || !statementBatchId.trim()} className="px-4 py-2 rounded bg-[rgb(var(--color-primary))] text-white text-tok-sm font-medium hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50">
          {loading ? 'Working…' : 'Create payroll export'}
        </button>
      </div>

      <section className="bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Payroll Export Batches</h3>
          <button type="button" onClick={loadBatches} disabled={loading} className="px-3 py-1 rounded border border-[rgb(var(--color-border))] text-[rgb(var(--color-text))] text-tok-xs font-medium hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50">Refresh</button>
        </div>
        {loading && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading payroll export batches…</p>}
        {!loading && batches.length === 0 && <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">No payroll export batches returned.</p>}
        {batches.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[rgb(var(--color-border))] text-tok-sm">
              <thead className="bg-[rgb(var(--color-surface-muted))]">
                <tr>
                  {['Batch ID', 'Status', 'Format', 'Creator', 'Action'].map((h) => (
                    <th key={h} className="px-4 py-2 text-left text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgb(var(--color-border))]">
                {batches.map((item) => (
                  <tr key={item.payroll_export_batch_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                    <td className="px-4 py-2 font-mono text-tok-xs text-[rgb(var(--color-text))]">{item.payroll_export_batch_id}</td>
                    <td className="px-4 py-2 text-[rgb(var(--color-text))]">{item.export_status}</td>
                    <td className="px-4 py-2 text-[rgb(var(--color-text))]">{item.export_format}</td>
                    <td className="px-4 py-2 text-tok-xs text-[rgb(var(--color-text))]">{item.created_by || item.created_by_user_id || '—'}</td>
                    <td className="px-4 py-2">
                      <button type="button" onClick={() => loadSingleBatch(item.payroll_export_batch_id)} className="px-3 py-1 rounded border border-[rgb(var(--color-border))] text-[rgb(var(--color-text))] text-tok-xs font-medium hover:bg-[rgb(var(--color-surface-muted))]">Select</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <article className="bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] rounded-lg p-6">
        <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))] mb-4">Selected Export</h3>
        {selectedBatch ? (
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-tok-sm mb-4">
            <dt className="font-medium text-[rgb(var(--color-text-muted))]">Batch ID</dt>
            <dd className="text-[rgb(var(--color-text))] font-mono text-tok-xs">{selectedBatch.payroll_export_batch_id}</dd>
            <dt className="font-medium text-[rgb(var(--color-text-muted))]">Status</dt>
            <dd className="text-[rgb(var(--color-text))]">{status}</dd>
            <dt className="font-medium text-[rgb(var(--color-text-muted))]">Format</dt>
            <dd className="text-[rgb(var(--color-text))]">{selectedBatch.export_format || exportFormat}</dd>
            <dt className="font-medium text-[rgb(var(--color-text-muted))]">Created By</dt>
            <dd className="text-[rgb(var(--color-text))] text-tok-xs">{createdBy || '—'}</dd>
            <dt className="font-medium text-[rgb(var(--color-text-muted))]">Approved By</dt>
            <dd className="text-[rgb(var(--color-text))] text-tok-xs">{selectedBatch.approved_by || selectedBatch.approved_by_user_id || '—'}</dd>
            <dt className="font-medium text-[rgb(var(--color-text-muted))]">Export URL</dt>
            <dd className="text-[rgb(var(--color-text))] text-tok-xs break-all">{selectedBatch.export_url || '—'}</dd>
          </dl>
        ) : (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic mb-4">No payroll export selected.</p>
        )}
        {approvalActorWarning && status === 'draft' && (
          <p className="mb-3 flex items-center gap-2 rounded border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/10 px-3 py-2 text-tok-xs text-[rgb(var(--color-warning))]">
            <span>⚠</span>
            You created this batch — a second comp admin must approve it. Ask another CA to log in and approve.
          </p>
        )}
        <div className="flex gap-2">
          <button type="button" disabled={!selectedBatch || status !== 'draft' || loading || approvalActorWarning} onClick={requestApprove} className="px-4 py-2 rounded bg-[rgb(var(--color-primary))] text-white text-tok-sm font-medium hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50">Approve</button>
          <button type="button" disabled={!selectedBatch || status !== 'approved' || loading} onClick={requestExport} className="px-4 py-2 rounded bg-[rgb(var(--color-success))] text-white text-tok-sm font-medium hover:bg-[rgb(var(--color-success))]/80 disabled:opacity-50">Export</button>
          {status === 'exported' && (
            selectedBatch?.export_url
              ? (
                <a
                  href={selectedBatch.export_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 rounded border border-[rgb(var(--color-border))] text-[rgb(var(--color-text))] text-tok-sm font-medium hover:bg-[rgb(var(--color-surface-muted))] inline-flex items-center gap-1"
                >
                  ↓ Re-download
                </a>
              )
              : (
                <p className="text-tok-sm text-[rgb(var(--color-warning))] bg-[rgb(var(--color-warning))]/10 border border-[rgb(var(--color-warning))]/30 rounded px-3 py-2">
                  Export complete but download URL is not yet available. Refresh the batch to retrieve the download link.
                </p>
              )
          )}
        </div>
      </article>

      {confirmation && (
        <div className="fixed inset-0 bg-[rgb(var(--color-text))]/40 flex items-center justify-center z-50" role="dialog" aria-modal="true">
          <div className="bg-[rgb(var(--color-surface))] rounded-lg shadow-xl p-6 max-w-md w-full space-y-4">
            <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">{confirmation.title}</h3>
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">{confirmation.message}</p>
            {approvalActorWarning && (
              <p className="text-tok-xs text-[rgb(var(--color-warning))] bg-[rgb(var(--color-warning))]/10 border border-[rgb(var(--color-warning))]/30 rounded px-3 py-2">
                SELF_APPROVAL_BLOCKED is handled explicitly if returned by the server.
              </p>
            )}
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setConfirmation(null)} className="px-4 py-2 rounded border border-[rgb(var(--color-border))] text-[rgb(var(--color-text))] text-tok-sm font-medium hover:bg-[rgb(var(--color-surface-muted))]">Cancel</button>
              <button type="button" disabled={loading} onClick={confirmation.onConfirm} className="px-4 py-2 rounded bg-[rgb(var(--color-primary))] text-white text-tok-sm font-medium hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50">{confirmation.confirmLabel}</button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
